import React, { useState, useMemo, useEffect, useRef } from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";

import {
  setProductID,
  setSalePrice,
  setQuantity,
  selectVarient,
} from "../store/slices/selectVarient";
import { connect, useDispatch, useSelector } from "react-redux";
import { selectUser } from "../features/auth/authSlice";
import AddTaskOutlinedIcon from "@mui/icons-material/AddTaskOutlined";
import {
  addToCartsAsync,
  addToPreorderCartsAsync,
  fetchAllAdminUsersCartsAsync,
  fetchAllCartsAsync,
  fetchAllPreorderCartsAsync,
  fetchAllUserCartsAsync,
  removeFromCartsAsync,
  selectAllAdminUserCart,
  selectAllAdminUserCarts,
  selectAllCarts,
  selectAllPreorders,
} from "../features/cartPage/cartPageSlice";

import { useNavigate } from "react-router";
import { findqty } from "../data";
import {
  calculateDealerPrice,
  calculateUpdatedDiscountPercentage,
  capitalizeWords,
  colourNameToHex,
  formatWithCommasWithDecimal,
} from "../utils/utils";
import { SwipeableDrawer } from "@mui/material";
import { trackEvent } from "../analytics/gtag";
import { EVENTS } from "../analytics/events";
import { ChevronDown, Minus, Plus } from "lucide-react";
import PriceBreakDown from "./PriceBreakDown";
import { selectIsOverlayVisible, toggleOverlay } from "../store/slices/headerSlice";
import SwipeToAction from "../mobileComponent/MobileDescriptionPage/SwipeToAction";

const AdvantageItem = ({ image, text }) => (
  <div className="flex items-center justify-start mr-1 mb-1 p-1 bg-gray-200 rounded-[25px] max-w-[250px] h-[30px]">
    <img
      loading="lazy"
      src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${process.env.REACT_APP_IMAGE_BASE}_ADVANTAGE/${image}`}
      alt={text}
      className="w-[25px] h-[25px] rounded-full p-[2px] mr-1 flex-shrink-0"
    />
    <span className="text-[14px] font-sans font-semibold text-black/80 truncate overflow-hidden whitespace-nowrap flex-1 leading-none">
      {text}
    </span>
  </div>
);

const VariantSelection = ({
  openMobileVarient,
  setOpenMobileVarient,
  openBottomSheet,
  product,
  qtys,
  toggleOverlay
}) => {
  const containerRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userDetail = useSelector(selectUser);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectColor, setSelectColor] = useState(null);
  const cart = useSelector(selectAllCarts);
  const preOrder = useSelector(selectAllPreorders);
  const [qty, setQty] = useState(0); // Initialize quantity to 1
  const [inputQty, setInputQty] = useState("0"); // Input field value
  const variations = useMemo(() => product?.skuDetails || [], [product]);
  const [selectedProduct, setSelectedProduct] = useState(
    [product?.skuDetails[0]] || []
  );
  const mainQty = useSelector(selectVarient).quantity;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState("");
  const [isCartValueZero, setIsCartValueZero] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  // For ADMIN ONLY
  const allCartsUser = useSelector(selectAllAdminUserCart);
  const allUserCartsItems = useSelector(selectAllAdminUserCarts);
  const [selectedUser, setSelectedUser] = useState(userDetail?.UserID || null);

  const handleSelectChange = (event) => {
    event.stopPropagation();
    setSelectedUser(event.target.value);
  };

  useEffect(() => {
    if (userDetail?.UserID && process.env.REACT_APP_ADMIN_USER?.split(",")?.map(Number)?.includes(userDetail?.UserID)) {
      handleOpen();
      dispatch(fetchAllAdminUsersCartsAsync()).then(() => {
        handleClose();
      });
    }
  }, [dispatch, userDetail?.UserID]);

  // Use useEffect to trigger the function when selectedUser changes
  useEffect(() => {
    const fetchOrders = async () => {
      if (selectedUser) {
        await dispatch(fetchAllUserCartsAsync(selectedUser)).then(() => {
          handleClose();
        });
      }
    };
    fetchOrders();
  }, [dispatch, selectedUser]);

  useEffect(() => {
    if (userDetail) {
      dispatch(fetchAllPreorderCartsAsync(userDetail?.UserID));
    }
  }, [dispatch, userDetail]);

  useEffect(() => {
    setSelectedProduct([product.skuDetails[0]] || []);
  }, [product, setSelectedProduct]);

  const handleIncreaseQty = () => {
    const bundleSize = parseInt(product && product?.Bundle);
    if (product && product?.manufacture_name === "ESI") {
      setQty(qty + bundleSize);
      setInputQty(String(qty + bundleSize));
    } else {
      const updateqty = parseInt(
        mainQty % parseInt(product && product?.Bundle)
      );
      const newQuantity = Math.min(qty + bundleSize, mainQty - updateqty || 0);
      setQty(newQuantity);
      setInputQty(String(newQuantity));
    }
  };

  const handleDecreaseQty = () => {
    const bundleSize = parseInt(product && product?.Bundle);
    const newQuantity = Math.max(qty - bundleSize, 0);
    setQty(newQuantity);
    setInputQty(String(newQuantity));
  };

  const quantity = findqty(product.skuDetails[0].SKU_ID, qtys) || 0;

  const handleInputChange = (event) => {
    const input = event.target.value;
    setInputQty(input);

    if (product && product?.manufacture_name === "ESI") {
      setQty(input);
    } else {
      // Validate and update quantity based on the product quantity
      const updateqty = parseInt(
        mainQty % parseInt(product && product?.Bundle)
      );
      const newQuantity = Math.min(
        parseInt(input) || 0,
        mainQty - updateqty || 0
      );
      setQty(newQuantity);
    }
  };

  const handleInputBlur = () => {
    if (product && product?.manufacture_name === "ESI") {
      const bundleSize = parseInt(product && product?.Bundle);
      let newQuantity = qty || 0;
      // Adjust new quantity to be a multiple of the bundle size
      newQuantity = Math.floor(newQuantity / bundleSize) * bundleSize;
      setQty(newQuantity);

      setInputQty(String(newQuantity));
    } else {
      // If the user enters a quantity beyond the product quantity, adjust it

      const bundleSize = parseInt(product && product?.Bundle);
      let newQuantity = Math.min(qty, mainQty || 0);
      // Adjust new quantity to be a multiple of the bundle size
      newQuantity = Math.floor(newQuantity / bundleSize) * bundleSize;
      setQty(newQuantity);

      setInputQty(String(newQuantity));
    }
  };

  // Initialize selectedSize and selectColor with the most available size and color
  useEffect(() => {
    const mostAvailableSize = variations.reduce((acc, variant) => {
      if (!acc || findqty(variant.SKU_ID, qtys) > findqty(acc.SKU_ID, qtys)) {
        return variant.size_name;
      }
      return acc;
    }, null);

    const mostAvailableColor = variations.reduce((acc, variant) => {
      if (!acc || findqty(variant.SKU_ID, qtys) > findqty(acc.SKU_ID, qtys)) {
        return variant.colour_name;
      }
      return acc;
    }, null);

    // setSelectedSize(mostAvailableSize);
    // setSelectColor(mostAvailableColor);
  }, [variations, qtys]);

  useEffect(() => {
    if (product && product.prod_ID !== null) {
      dispatch(setSalePrice(product.skuDetails[0].NDP_Price));
      dispatch(setQuantity(findqty(product.skuDetails[0].SKU_ID, qtys)));
      dispatch(setProductID(product.prod_ID));
    }
    if (product && product?.skuDetails?.length === 1) {
      setSelectedSize(product?.skuDetails[0]?.size_name);
      setSelectColor(product?.skuDetails[0]?.colour_name);
    }
  }, [dispatch, product]);

  useEffect(() => {
    if (qty > 0) {
      setIsCartValueZero(false);
    }
  }, [qty]);

  useEffect(() => {
    const sizes = [...new Set(variations.map((variant) => variant.size_name))];
    const colors = [
      ...new Set(variations.map((variant) => variant.colour_name)),
    ];
    setAvailableSizes(sizes);
    setAvailableColors(colors);
  }, [variations]);

  useEffect(() => {
    let filteredSizes = [];
    let filteredColors = [];

    if (!selectedSize && !selectColor) {
      filteredSizes = [
        ...new Set(variations.map((variant) => variant.size_name)),
      ];
      filteredColors = [
        ...new Set(variations.map((variant) => variant.colour_name)),
      ];
      setSelectedProduct([variations[0]]);
    } else {
      if (selectedSize && selectColor) {
        const selectedSku = variations.find(
          (variant) =>
            variant.size_name === selectedSize &&
            variant.colour_name === selectColor
        );

        if (selectedSku) {
          dispatch(setSalePrice(selectedSku?.Sale_Price));
          dispatch(setQuantity(findqty(selectedSku?.SKU_ID, qtys)));
          const selectProduct = variations.filter(
            (variant) =>
              variant.size_name === selectedSize &&
              variant.colour_name === selectColor
          );
          setSelectedProduct(selectProduct);
          if (qty > findqty(selectedSku?.SKU_ID, qtys)) {
            setQty(findqty(selectedSku?.SKU_ID, qtys));
          }
        }
      } else if (selectedSize && !selectColor) {
        const selectedSku = variations.find(
          (variant) => variant.size_name === selectedSize
        );

        if (selectedSku) {
          dispatch(setSalePrice(selectedSku?.Sale_Price));
          dispatch(setQuantity(findqty(selectedSku?.SKU_ID, qtys)));
          const selectProduct = variations.filter(
            (variant) => variant.size_name === selectedSize
          );
          setSelectedProduct(selectProduct);
          if (qty > findqty(selectedSku?.SKU_ID, qtys)) {
            setQty(findqty(selectedSku?.SKU_ID, qtys));
          }
        }
      } else if (!selectedSize && selectColor) {
        const selectedSku = variations.find(
          (variant) => variant.colour_name === selectColor
        );

        if (selectedSku) {
          dispatch(setSalePrice(selectedSku?.Sale_Price));
          dispatch(setQuantity(findqty(selectedSku?.SKU_ID, qtys)));
          const selectProduct = variations.filter(
            (variant) => variant.colour_name === selectColor
          );
          setSelectedProduct(selectProduct);
          if (qty > findqty(selectedSku?.SKU_ID, qtys)) {
            setQty(findqty(selectedSku?.SKU_ID, qtys));
          }
        }
      }

      filteredSizes = availableSizes;
      filteredColors = availableColors;

      if (selectedSize) {
        filteredColors = variations
          .filter((variant) => variant.size_name === selectedSize)
          .map((variant) => variant.colour_name);
      }

      if (selectColor) {
        filteredSizes = variations
          .filter((variant) => variant.colour_name === selectColor)
          .map((variant) => variant.size_name);
      }
    }

    setAvailableSizes([...new Set(filteredSizes)]);
    setAvailableColors([...new Set(filteredColors)]);
  }, [selectedSize, selectColor, variations]);

  const handleSelectSize = (size) => {
    setSelectedSize(size);
  };

  const handleSelectColor = (color) => {
    setSelectColor(color);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside the container
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpenMobileVarient(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [containerRef, setOpenMobileVarient]);

  // ADD TO CART
  const addToCart = (selectedQty) => {
    if (
      availableSizes &&
      availableSizes[0] !== null &&
      availableColors &&
      availableColors[0] !== null
    ) {
      if (selectedSize && selectColor) {
        setLoading("cart");
        let cartData = {
          userID:
            userDetail?.UserID &&
              process.env.REACT_APP_ADMIN_USER?.split(",")?.map(Number)?.includes(userDetail?.UserID) &&
              selectedUser !== null
              ? selectedUser
              : userDetail?.UserID,
          SKU: selectedProduct[0].SKU_ID,
          buyQty: selectedQty === 0 ? product?.Bundle : selectedQty,
        };
        if (selectedQty >= 1) {
          dispatch(addToCartsAsync(cartData)).then(() => {
            // analytics
            trackEvent({
              action: EVENTS.ADD_TO_CART,
              category: "Cart",
              label: `User Added Product-> SKU: ${selectedProduct[0].SKU_ID}`,
              value: userDetail?.PhoneNumber,
            });
            dispatch(fetchAllCartsAsync(userDetail?.UserID)).then(() => {
              if (
                userDetail?.UserID &&
                process.env.REACT_APP_ADMIN_USER?.split(",")?.map(Number)?.includes(userDetail?.UserID) &&
                selectedUser !== null
              ) {
                dispatch(fetchAllUserCartsAsync(selectedUser));
              }
              setLoading("");
            });
          });
        } else if (selectedQty === 0) {
          setIsCartValueZero(true);
          setLoading("");
        } else {
          setLoading("");
        }
      } else {
        return;
      }
    } else if (
      availableSizes &&
      availableSizes[0] !== null &&
      availableColors[0] === null
    ) {
      if (selectedSize !== null) {
        setLoading("cart");
        let cartData = {
          userID:
            userDetail?.UserID &&
              process.env.REACT_APP_ADMIN_USER?.split(",")?.map(Number)?.includes(userDetail?.UserID) &&
              selectedUser !== null
              ? selectedUser
              : userDetail?.UserID,
          SKU: selectedProduct[0].SKU_ID,
          buyQty: selectedQty === 0 ? product?.Bundle : selectedQty,
        };
        if (selectedQty >= 1) {
          dispatch(addToCartsAsync(cartData)).then(() => {
            // analytics
            trackEvent({
              action: EVENTS.ADD_TO_CART,
              category: "Cart",
              label: `User Added Product-> SKU: ${selectedProduct[0].SKU_ID}`,
              value: userDetail?.PhoneNumber,
            });
            dispatch(fetchAllCartsAsync(userDetail?.UserID)).then(() => {
              if (
                userDetail?.UserID &&
                process.env.REACT_APP_ADMIN_USER?.split(",")?.map(Number)?.includes(userDetail?.UserID) &&
                selectedUser !== null
              ) {
                dispatch(fetchAllUserCartsAsync(selectedUser));
              }
              setLoading("");
            });
          });
        } else if (selectedQty === 0) {
          setIsCartValueZero(true);
          setLoading("");
        } else {
          setLoading("");
        }
      }
    } else if (
      availableSizes &&
      availableSizes[0] === null &&
      availableColors &&
      availableColors[0] !== null
    ) {
      if (selectColor) {
        setLoading("cart");
        let cartData = {
          userID:
            userDetail?.UserID &&
              process.env.REACT_APP_ADMIN_USER?.split(",")?.map(Number)?.includes(userDetail?.UserID) &&
              selectedUser !== null
              ? selectedUser
              : userDetail?.UserID,
          SKU: selectedProduct[0].SKU_ID,
          buyQty: selectedQty === 0 ? product?.Bundle : selectedQty,
        };
        if (selectedQty >= 1) {
          dispatch(addToCartsAsync(cartData)).then(() => {
            // analytics
            trackEvent({
              action: EVENTS.ADD_TO_CART,
              category: "Cart",
              label: `User Added Product-> SKU: ${selectedProduct[0].SKU_ID}`,
              value: userDetail?.PhoneNumber,
            });
            dispatch(fetchAllCartsAsync(userDetail?.UserID)).then(() => {
              if (
                userDetail?.UserID &&
                process.env.REACT_APP_ADMIN_USER?.split(",")?.map(Number)?.includes(userDetail?.UserID) &&
                selectedUser !== null
              ) {
                dispatch(fetchAllUserCartsAsync(selectedUser));
              }
              setLoading("");
            });
          });
        } else if (selectedQty === 0) {
          setIsCartValueZero(true);
          setLoading("");
        } else {
          setLoading("");
        }
      }
    } else {
      return;
    }
  };

  const addToCartHandler = () => {
    addToCart(qty);
  }

  const handleBuyNow = (selectedQty) => {
    if (isInCart()) {
      navigate("/carts");
      // analytics
      trackEvent({
        action: EVENTS.CART_STEP,
        category: "Cart Step",
        label: `User Enter Cart Step`,
        value: userDetail?.PhoneNumber,
      });
    } else {

      if (
        availableSizes &&
        availableSizes[0] !== null &&
        availableColors &&
        availableColors[0] !== null
      ) {
        if (selectedSize && selectColor) {
          setLoading("buy");
          let cartData = {
            userID: userDetail?.UserID,
            SKU: selectedProduct[0].SKU_ID,
            buyQty: selectedQty === 0 ? product?.Bundle : selectedQty,
          };
          if (selectedQty >= 1) {
            dispatch(addToCartsAsync(cartData)).then(() => {
              // analytics
              trackEvent({
                action: EVENTS.ADD_TO_CART,
                category: "Cart",
                label: `User Added Product-> SKU: ${selectedProduct[0].SKU_ID}`,
                value: userDetail?.PhoneNumber,
              });
              dispatch(fetchAllCartsAsync(userDetail?.UserID)).then(() => {
                setLoading("");
                navigate("/carts");
                // analytics
                trackEvent({
                  action: EVENTS.CART_STEP,
                  category: "Cart Step",
                  label: `User Enter Cart Step`,
                  value: userDetail?.PhoneNumber,
                });
              });
            });
          } else if (selectedQty === 0) {
            setIsCartValueZero(true);
            setLoading("");
          } else {
            setLoading("");
          }
        } else {
          return;
        }
      } else if (
        availableSizes &&
        availableSizes[0] !== null &&
        availableColors[0] === null
      ) {
        if (selectedSize !== null) {
          setLoading("Buy");
          let cartData = {
            userID: userDetail?.UserID,
            SKU: selectedProduct[0].SKU_ID,
            buyQty: selectedQty === 0 ? product?.Bundle : selectedQty,
          };
          if (selectedQty >= 1) {
            dispatch(addToCartsAsync(cartData)).then(() => {
              // analytics
              trackEvent({
                action: EVENTS.ADD_TO_CART,
                category: "Cart",
                label: `User Added Product-> SKU: ${selectedProduct[0].SKU_ID}`,
                value: userDetail?.PhoneNumber,
              });
              dispatch(fetchAllCartsAsync(userDetail?.UserID)).then(() => {
                setLoading("");
                navigate("/carts");
                // analytics
                trackEvent({
                  action: EVENTS.CART_STEP,
                  category: "Cart Step",
                  label: `User Enter Cart Step`,
                  value: userDetail?.PhoneNumber,
                });
              });
            });
          } else if (selectedQty === 0) {
            setIsCartValueZero(true);
            setLoading("");
          } else {
            setLoading("");
          }
        }
      } else if (
        availableSizes &&
        availableSizes[0] === null &&
        availableColors &&
        availableColors[0] !== null
      ) {
        if (selectColor) {
          setLoading("Buy");
          let cartData = {
            userID: userDetail?.UserID,
            SKU: selectedProduct[0].SKU_ID,
            buyQty: selectedQty === 0 ? product?.Bundle : selectedQty,
          };
          if (selectedQty >= 1) {
            dispatch(addToCartsAsync(cartData)).then(() => {
              // analytics
              trackEvent({
                action: EVENTS.ADD_TO_CART,
                category: "Cart",
                label: `User Added Product-> SKU: ${selectedProduct[0].SKU_ID}`,
                value: userDetail?.PhoneNumber,
              });
              dispatch(fetchAllCartsAsync(userDetail?.UserID)).then(() => {
                setLoading("");
                navigate("/carts");
                // analytics
                trackEvent({
                  action: EVENTS.CART_STEP,
                  category: "Cart Step",
                  label: `User Enter Cart Step`,
                  value: userDetail?.PhoneNumber,
                });
              });
            });
          } else if (selectedQty === 0) {
            setIsCartValueZero(true);
            setLoading("");
          } else {
            setLoading("");
          }
        }
      } else {
        return;
      }
    }
  };

  const buyNowHandler = () => {
    handleBuyNow(qty);
  }

  // Function to check if a product is in the cart
  const isInCart = () => {
    if (userDetail?.UserID && process.env.REACT_APP_ADMIN_USER?.split(",")?.map(Number)?.includes(userDetail?.UserID)) {
      return (
        Array.isArray(allUserCartsItems) &&
        allUserCartsItems?.some(
          (item) => item?.SKU_ID === selectedProduct[0]?.SKU_ID
        )
      );
    } else {
      return (
        Array.isArray(cart) &&
        cart?.some(
          (item) => item?.skuDetails?.SKU_ID === selectedProduct[0]?.SKU_ID
        )
      );
    }
  };

  // REMOVE FROM CART
  const removeFromCart = () => {
    setLoading("Cart");
    let cartData = {
      userID:
        userDetail?.UserID &&
          process.env.REACT_APP_ADMIN_USER?.split(",")?.map(Number)?.includes(userDetail?.UserID) &&
          selectedUser !== null
          ? selectedUser
          : userDetail?.UserID,
      SKU: selectedProduct[0]?.SKU_ID,
    };
    dispatch(removeFromCartsAsync(cartData)).then(() => {
      // analytics
      trackEvent({
        action: EVENTS.REMOVE_FROM_CART,
        category: "Cart",
        label: `User Remove Product-> SKU: ${selectedProduct[0]?.SKU_ID}`,
        value: userDetail?.PhoneNumber,
      });
      dispatch(fetchAllCartsAsync(userDetail?.UserID)).then(() => {
        if (
          userDetail?.UserID &&
          process.env.REACT_APP_ADMIN_USER?.split(",")?.map(Number)?.includes(userDetail?.UserID) &&
          selectedUser !== null
        ) {
          dispatch(fetchAllUserCartsAsync(selectedUser));
        }
        setLoading("");
      });
    });
  };

  const handlenavigate = (prod_id) => {
    navigate(`/product/${prod_id}`);
    // Analytics
    trackEvent({
      action: EVENTS.VIEW_PRODUCT,
      category: "Product",
      label: `User Visited Product ${prod_id}`,
      value: userDetail?.PhoneNumber,
    });
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setOpenMobileVarient(open);
  };


  const discount = calculateUpdatedDiscountPercentage(
    selectedProduct[0]?.MRP_price,
    parseInt(selectedProduct[0]?.NDP_Price),
    product?.GST
  );


  const advantages = product && product?.Advantages;


  // ▼▼ Add inside component, near other state hooks ▼▼
  const [showPriceDetails, setShowPriceDetails] = useState(false);

  const totalAmount = useMemo(() => {
    if (!selectedProduct) return 0;
    const price = calculateDealerPrice(
      parseInt(selectedProduct[0]?.MRP_price),
      parseInt(selectedProduct[0]?.NDP_Price),
      product?.GST
    );
    return (price * inputQty).toFixed(2);
  }, [selectedProduct, inputQty]);


  return (
    <SwipeableDrawer
      anchor="bottom"
      open={openMobileVarient}
      onClose={() => toggleDrawer(null)}
      // onOpen={() => toggleDrawer(true)}
      disableAutoFocus
      disableEnforceFocus
      disableEscapeKeyDown
      disableRestoreFocus
      PaperProps={{
        sx: {
          borderTopLeftRadius: "15px",
          borderTopRightRadius: "15px",
          overflow: "visible",
        },
      }}
    >
      <div
        className="w-full max-h-[80vh] overflow-y-auto bg-white-1 rounded-t-3xl px-3 pt-2 pb-3 text-black-1 z-[1200]"
        ref={containerRef}
      >
        <div className="w-full h-[40px] border-b-2 border-black-1/15 flex items-center justify-between px-3">
          <span className="text-16 font-bold text-blue">Select Variant</span>
          <CancelIcon
            onClick={() => setOpenMobileVarient(null)}
            className="text-blue"
            style={{ fontSize: "20px" }}
          />
        </div>
        <div className="flex items-start gap-4 border-b border-gray-200 pb-2 my-2">
          {/* Product Image */}
          <div className="flex-shrink-0 w-[100px] h-[90px] rounded-sm overflow-hidden bg-gray-50 flex items-center justify-center shadow-sm"
            onClick={() => handlenavigate(product.prod_ID)}>
            <img
              src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${process.env.REACT_APP_IMAGE_BASE}_PRODUCTS_IMAGE/${product?.images[0] && product?.images[0]
                }`}
              alt="product-img"
              className="w-full h-full object-contain transition-transform duration-300"
            />
          </div>

          {/* Product Details */}
          <div className="flex flex-col flex-1">
            {/* Manufacturer */}
            <div className="flex items-center">
              {product && product?.manufacture_images ? (
                <img
                  loading="lazy"
                  className="h-5 object-contain"
                  src={`https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${process.env.REACT_APP_IMAGE_BASE}_MANUFACTURE_IMAGE/${product && product?.manufacture_images
                    }`}
                  alt="brand-logo"
                />
              ) : (
                <span className="text-[13px] text-gray-500 italic">
                  By {product?.manufacture_name && product?.manufacture_name}
                </span>
              )}
            </div>
            <h2 className="mt-2 text-[15px] font-semibold leading-snug text-gray-900 line-clamp-2">
              {selectedProduct[0] && selectedProduct[0]?.prod_name}
            </h2>

            {/* Pricing */}
            <div className="mt-1 w-full flex items-center  gap-5 justify-start">
              <div className="flex flex-col items-start justify-start">
                <p className="font-semibold text-[15px] text-gray-800">
                  Sale Price – <span className="text-[18px]">
                    ₹
                    {selectedProduct[0] &&
                      selectedProduct[0]?.NDP_Price &&
                      formatWithCommasWithDecimal(
                        calculateDealerPrice(
                          parseInt(
                            selectedProduct[0] && selectedProduct[0]?.NDP_Price
                          ),
                          parseInt(
                            selectedProduct[0] && selectedProduct[0]?.NDP_Price
                          ),
                          product && product?.GST
                        )
                      )}
                  </span>
                </p>
                <p className="text-[13px] text-gray-500 mt-[2px]">
                  Product MRP – ₹ <span className="line-through">{selectedProduct[0] &&
                    selectedProduct[0]?.MRP_price &&
                    formatWithCommasWithDecimal(
                      selectedProduct[0] && selectedProduct[0]?.MRP_price
                    )}</span>
                </p>
              </div>

              <p className="w-fit text-[12px] px-3 py-1 rounded-md bg-peach  font-semibold text-white-1">
                {discount}
                % OFF
              </p>

            </div>
          </div>
        </div>
        {advantages && (
          <div
            className="w-full flex flex-col items-start justify-start py-1 pl-3 border-b-2 border-black-1/15"
          >
            <div className="w-full flex items-center justify-start flex-wrap mt-1">
              {advantages?.Advantage1 && (
                <AdvantageItem
                  image={advantages?.Image1}
                  text={advantages?.Advantage1}
                />
              )}
              {advantages?.Advantage2 && (
                <AdvantageItem
                  image={advantages?.Image2}
                  text={advantages?.Advantage2}
                />
              )}
              {advantages?.Advantage3 && (
                <AdvantageItem
                  image={advantages?.Image3}
                  text={advantages?.Advantage3}
                />
              )}
              {advantages?.Advantage4 && (
                <AdvantageItem
                  image={advantages?.Image4}
                  text={advantages?.Advantage4}
                />
              )}
            </div>
          </div>
        )}

        {availableSizes &&
          availableSizes[0] !== null &&
          availableSizes?.length > 0 && (
            <div className="mt-3">
              <p className="text-[14px] font-bold mb-2 text-gray-700">Choose Size:</p>
              {availableSizes && availableSizes[0] !== undefined && (
                <div className="flex gap-2 flex-wrap ml-2">
                  {availableSizes && availableSizes?.map((size) => (
                    <button
                      key={size}
                      onClick={() => handleSelectSize(size)}
                      className={`px-4 py-1.5 text-sm rounded-[2px] border-2 transition-all duration-150 font-medium ${selectedSize === size
                        ? "border-peach text-black-1"
                        : "border-gray-300 text-gray-700 hover:border-gray-400"
                        }`}
                    >
                      {capitalizeWords(size)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

        {availableColors &&
          availableColors[0] !== null &&
          availableColors?.length > 0 && (
            <div className="mt-3">
              <p className="text-[14px] font-bold mb-2 text-gray-700">Choose Color:</p>
              {availableColors && availableColors[0] !== undefined && (
                <div className="flex gap-3 items-center flex-wrap ml-2">
                  {availableColors.map((color) => (
                    <button
                      key={color}
                      onClick={() => handleSelectColor(color)}
                      className={`w-9 h-9 rounded-full flex items-center justify-center border transition-transform duration-150 ${selectColor === color
                        ? "scale-110 border-2 border-peach shadow-md"
                        : "border-none"
                        }`}
                      style={{
                        backgroundColor:
                          selectColor === color ? "white" : "transparent",
                      }}
                    >
                      <div
                        className="w-7 h-7 rounded-full"
                        style={{
                          backgroundColor: colourNameToHex(color),
                        }}
                      />
                    </button>
                  ))}
                </div>)}
            </div>
          )}

        <div className="flex flex-col items-start justify-start">
          <div className="mt-3">
            <p className="text-[14px] font-bold mb-2 text-gray-700">Choose Quantity:</p>
            {/* Quantity Selector */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleDecreaseQty}
                className={`w-8 h-8 flex items-center justify-center rounded-[2px] transition-all duration-150 ${inputQty < product?.Bundle || quantity <= 0
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-blue text-white-1 hover:bg-blue"
                  }`}
              >
                <Minus size={18} />
              </button>

              <input
                type="number"
                disabled={quantity <= 0}
                className={`w-12 h-8 text-center border ${isCartValueZero ? "border-peach" : "border-gray-300"
                  } text-gray-800 rounded-[2px] focus:ring-2 focus:ring-blue outline-none ${quantity <= 0 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : ""
                  }`}
                value={inputQty}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
              />

              <button
                onClick={handleIncreaseQty}
                disabled={quantity <= product?.Bundle}
                className={`w-8 h-8 flex items-center justify-center rounded-[2px] transition-all duration-150 ${quantity <= 0 || inputQty >= quantity
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-blue text-white-1 hover:bg-blue"
                  }`}
              >
                <Plus size={18} />
              </button>
            </div>
          </div>
          {/* Stock Status */}
          <div className="mt-3 flex flex-col items-start">
            {quantity <= product?.Bundle ? (
              <div className="flex items-center text-red-500 font-medium text-sm mb-2">
                <span className="mr-1">⚠️</span> Out of Stock
              </div>
            ) : inputQty >= quantity ? (
              <div className="flex flex-col items-start gap-2">
                <span className="text-xs text-gray-700 font-medium">
                  *Only {quantity} units in stock!
                </span>
                <button className="ml-2 px-3 whitespace-nowrap py-[3px] bg-orange-500 text-white text-xs rounded-full shadow-sm hover:bg-orange-600 transition-all duration-150">
                  Request more
                </button>
              </div>
            ) : (
              <div className="flex items-center text-green-600 font-semibold text-sm mb-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-4 h-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                In Stock
              </div>
            )}

          </div>
        </div>

        {/* PRICE ACCORDION */}
        <div className="w-full mt-4 mb-3 px-3 py-3 bg-white-1 border border-gray-200 rounded-xl shadow-sm">

          {/* Header Row */}
          <button
            onClick={() => setShowPriceDetails(!showPriceDetails)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <p className="text-[14px] text-gray-500 font-medium">Total Amount</p>
              <p className="text-[20px] font-bold text-gray-900 mt-[2px]">
                ₹{totalAmount}
              </p>
            </div>

            {/* Icons */}
            <div className="text-blue">
              {showPriceDetails ? (
                <ChevronDown size={24} />
              ) : (
                <Plus size={24} />
              )}
            </div>
          </button>

          {/* Animated Expand */}
          {showPriceDetails && (
            <div className="mt-4 border-t pt-3 animate-fadeIn">
              <PriceBreakDown
                selectedProduct={selectedProduct}
                product={product}
              />
            </div>
          )}
        </div>

        <SwipeToAction
          addToCart={addToCartHandler}
          removeFromCart={removeFromCart}
          isInCart={isInCart}
          buyNowHandle={buyNowHandler}
          toggleOverlay={toggleOverlay}
          userDetail={userDetail}
          isBottomSheetOpen={openBottomSheet}
          isSwipeEnabled={
            (
              (!availableSizes || availableSizes?.filter(Boolean)?.length === 0) ||
              !!selectedSize
            ) &&
            (
              (!availableColors || availableColors?.filter(Boolean)?.length === 0) ||
              !!selectColor
            ) &&
            inputQty > 0
          }

        />
      </div>
    </SwipeableDrawer>
  );
};

const mapStateToProps = (state) => ({
  isOverlayVisible: selectIsOverlayVisible(state),
});

const mapDispatchToProps = {
  toggleOverlay,
};
export default connect(mapStateToProps, mapDispatchToProps)(VariantSelection);
